import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ImportYoungAnimals from 'shared/assets/src-assets/png/importungdyr.png';
import { GetSiteEntities } from 'shared/state/ducks/general-settings/operations';
import { MigrateDownstream } from 'shared/state/ducks/site/operations';
import { localized, localizedDynamic } from 'shared/state/i18n/i18n';
import { WebAppState } from 'web/state/store.web';
import { showAlert } from 'web/view/components/skiold-alert/skiold-alert';
import { SkioldCheckbox } from 'web/view/components/skiold-components/skiold-checkbox/skiold-checkbox';
import { ViewWeb } from 'web/view/components/utils/web-view';
import { useAdminSite } from './admin-site-provider';
import { GLOBAL_MODELS, resolveMigrationSelection } from './migration-dependencies';

interface PropsFromParent {}

const modelName = (model: string) => localizedDynamic(`SkioldModels.Values.${model}`);
const modelDescription = (model: string) => localizedDynamic(`SkioldModels.Description.${model}`);

// The checkbox is a pure indicator — the whole row owns the click (see .col-check pointer-events).
const noop = () => undefined;

type SortColumn = 'name' | 'description';

const SortArrow: FC<{ active: boolean; direction: 'asc' | 'desc' }> = ({ active, direction }) => (
	<span className={'migration-sort-arrow' + (active ? ' active' : '')}>
		{active ? (direction === 'asc' ? '▲' : '▼') : '↕'}
	</span>
);

export const SiteMigrationOverview: FC<PropsFromParent> = React.memo(() => {
	const dispatch = useDispatch();

	// context
	const { setActionButtons } = useAdminSite();

	// redux data
	const entities = useSelector((state: WebAppState) => state.generalSettings.siteEntities);
	const siteId = useSelector((state: WebAppState) => state.profile.active!.siteId);
	const isLoading = useSelector((state: WebAppState) => state.site.isLoading);

	// local state: only the models the user picked directly. Dependencies are derived.
	const [directSelected, setDirectSelected] = useState<string[]>([]);
	const [search, setSearch] = useState('');
	const [sortColumn, setSortColumn] = useState<SortColumn>('name');
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

	useEffect(() => {
		dispatch(GetSiteEntities());
	}, [dispatch]);

	// Expand the user's picks into the full closure (picks + transitive prerequisites).
	const resolved = useMemo(() => resolveMigrationSelection(directSelected), [directSelected]);
	const directSet = useMemo(() => new Set(directSelected), [directSelected]);
	const selectedAllSet = useMemo(() => new Set(resolved.all), [resolved.all]);

	const dependencyCount = resolved.all.length - directSelected.length;

	const rows = useMemo(() => {
		const query = search.trim().toLowerCase();
		const direction = sortDirection === 'asc' ? 1 : -1;
		return entities
			.map(model => ({ model, name: modelName(model), description: modelDescription(model) }))
			.filter(row =>
				query.length === 0
					? true
					: row.name.toLowerCase().includes(query) || row.description.toLowerCase().includes(query),
			)
			.sort((a, b) => direction * a[sortColumn].localeCompare(b[sortColumn]));
	}, [entities, search, sortColumn, sortDirection]);

	const toggle = useCallback((model: string) => {
		setDirectSelected(prev => (prev.includes(model) ? prev.filter(m => m !== model) : [...prev, model]));
	}, []);

	// "Check all" operates on the currently visible (filtered) rows, so it doubles as a
	// "select this filtered group" action. With no active filter, that is every model.
	const visibleModels = useMemo(() => rows.map(row => row.model), [rows]);
	const allVisibleSelected = useMemo(
		() => rows.length > 0 && rows.every(row => selectedAllSet.has(row.model)),
		[rows, selectedAllSet],
	);

	const toggleAll = useCallback(() => {
		setDirectSelected(prev => {
			if (allVisibleSelected) {
				const visibleSet = new Set(visibleModels);
				return prev.filter(model => !visibleSet.has(model));
			}
			const next = new Set(prev);
			visibleModels.forEach(model => next.add(model));
			return Array.from(next);
		});
	}, [allVisibleSelected, visibleModels]);

	const changeSort = useCallback(
		(column: SortColumn) => {
			if (sortColumn === column) {
				setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
			} else {
				setSortColumn(column);
				setSortDirection('asc');
			}
		},
		[sortColumn],
	);

	const startMigration = useCallback(() => {
		if (!siteId) {
			return;
		}
		dispatch(MigrateDownstream(siteId, resolved.all, showAlert));
	}, [resolved.all, siteId, dispatch]);

	useEffect(() => {
		if (setActionButtons) {
			setActionButtons([
				{
					title: localized('Migrate'),
					action: () => startMigration(),
					icon: ImportYoungAnimals,
					isLoading,
				},
			]);
		}
	}, [resolved.all, siteId, isLoading, startMigration, setActionButtons]);

	return (
		<ViewWeb className="site-migration-overview">
			<ViewWeb className="migration-toolbar">
				<ViewWeb className="migration-summary">
					{resolved.all.length === 0 ? (
						<span className="migration-summary-hint">{localized('MigrationSelectHint')}</span>
					) : (
						<>
							<span className="migration-summary-total">{resolved.all.length}</span>
							<span className="migration-summary-label">{localized('MigrationModelsToMigrate')}</span>
							<span className="migration-summary-breakdown">
								{directSelected.length} {localized('MigrationManuallySelected')}
								{dependencyCount > 0 && (
									<>
										{' + '}
										<span className="migration-dep-count">
											{dependencyCount} {localized('MigrationDependenciesAdded')}
										</span>
									</>
								)}
							</span>
						</>
					)}
				</ViewWeb>
				<input
					className="migration-search"
					type="text"
					value={search}
					placeholder={localized('MigrationSearchPlaceholder')}
					onChange={e => setSearch(e.target.value)}
				/>
			</ViewWeb>

			<ViewWeb className="migration-table">
				<ViewWeb className="migration-row migration-header-row">
					<ViewWeb
						className="migration-cell col-check header-check"
						onClick={rows.length > 0 ? toggleAll : undefined}
						title={localized('MigrationSelectAll')}
					>
						<SkioldCheckbox isChecked={allVisibleSelected} onClick={noop} />
					</ViewWeb>
					<ViewWeb className="migration-cell col-name sortable" onClick={() => changeSort('name')}>
						{localized('DataModel')}
						<SortArrow active={sortColumn === 'name'} direction={sortDirection} />
					</ViewWeb>
					<ViewWeb className="migration-cell col-desc sortable" onClick={() => changeSort('description')}>
						{localized('Description')}
						<SortArrow active={sortColumn === 'description'} direction={sortDirection} />
					</ViewWeb>
				</ViewWeb>

				{rows.length === 0 ? (
					<ViewWeb className="migration-empty">{localized('MigrationNoResults')}</ViewWeb>
				) : (
					rows.map(row => {
						const isDirect = directSet.has(row.model);
						const isDependency = !isDirect && selectedAllSet.has(row.model);
						const requiredByNames = (resolved.requiredBy[row.model] || []).map(modelName);
						return (
							<MigrationRow
								key={row.model}
								model={row.model}
								name={row.name}
								description={row.description}
								isDirect={isDirect}
								isDependency={isDependency}
								isGlobal={GLOBAL_MODELS.has(row.model)}
								requiredByNames={requiredByNames}
								onToggle={toggle}
							/>
						);
					})
				)}
			</ViewWeb>
		</ViewWeb>
	);
});

interface MigrationRowProps {
	model: string;
	name: string;
	description: string;
	isDirect: boolean;
	isDependency: boolean;
	isGlobal: boolean;
	requiredByNames: string[];
	onToggle: (model: string) => void;
}

const MigrationRow: FC<MigrationRowProps> = React.memo(
	({ model, name, description, isDirect, isDependency, isGlobal, requiredByNames, onToggle }) => {
		const handleToggle = useCallback(() => {
			if (!isDependency) {
				onToggle(model);
			}
		}, [isDependency, model, onToggle]);

		const rowClassName =
			'migration-row' + (isDirect ? ' is-direct' : '') + (isDependency ? ' is-dependency' : '');

		return (
			<ViewWeb
				className={rowClassName}
				onClick={handleToggle}
				title={isDependency ? localized('MigrationDependencyLocked') : undefined}
			>
				<ViewWeb className="migration-cell col-check">
					<SkioldCheckbox isChecked={isDirect || isDependency} disabled={isDependency} onClick={noop} />
				</ViewWeb>
				<ViewWeb className="migration-cell col-name">
					<span className="migration-model-name">
						{name}
						{isGlobal && (
							<span className="migration-global-badge" title={localized('MigrationGlobalHint')}>
								{localized('MigrationGlobalBadge')}
							</span>
						)}
					</span>
					{isDependency && requiredByNames.length > 0 && (
						<span className="migration-required-by">
							{localized('MigrationRequiredBy')}: {requiredByNames.join(', ')}
						</span>
					)}
				</ViewWeb>
				<ViewWeb className="migration-cell col-desc">{description}</ViewWeb>
			</ViewWeb>
		);
	},
);
